import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useMemo,
} from 'react';
import Script from 'next/script';
import { useTunnusContext } from 'contexts/TunnusContext';
import { apmRum } from 'services/apm-rum';
import {
  initSnowplowTracker,
  addConsentContext,
  setUser,
  clearUser,
  addUserProfile,
} from 'services/snowplowAnalytics/snowplowAnalytics';
import logger from 'services/logger';
import { useCookieConsent } from 'contexts/CookieConsentContext';
import { snowplowAnalyticsProperties } from 'services/properties/frontend';

declare global {
  interface Window {
    yleSnowplow?: (method: string, ...args: unknown[]) => void;
    yleSnowplowReady?: Promise<void>;
  }
}

type SnowplowContextType = {
  isSnowplowReady: boolean;
};

const SnowplowContext = createContext<SnowplowContextType>({
  isSnowplowReady: false,
});

type SnowplowProviderProps = {
  children: ReactNode;
};

export const SnowplowProvider: React.FC<SnowplowProviderProps> = ({
  children,
}) => {
  const consent = useCookieConsent();
  const {
    user: { transientId, isSubAccount, age } = {},
    isLoggedIn: isAuthenticated,
  } = useTunnusContext();

  const [isSnowplowScriptInPlace, setSnowplowScriptInPlace] = useState(false);
  const [isSnowplowInitialized, setSnowplowInitialized] = useState(false);
  const [isSnowplowUserReady, setSnowplowUserReady] = useState(false);
  const [isSnowplowConsentReady, setSnowplowConsentReady] = useState(false);

  const resolveRef = useRef<(() => void) | null>(null);
  const rejectRef = useRef<(() => void) | null>(null);

  const contextValue = useMemo(
    (): SnowplowContextType => ({
      isSnowplowReady:
        isSnowplowInitialized && isSnowplowUserReady && isSnowplowConsentReady,
    }),
    [isSnowplowInitialized, isSnowplowUserReady, isSnowplowConsentReady]
  );

  useEffect(() => {
    if (!window.yleSnowplowReady) {
      window.yleSnowplowReady = new Promise<void>((resolve, reject) => {
        resolveRef.current = resolve;
        rejectRef.current = reject;
      });
    }
  }, []);

  // init snowplow
  useEffect(() => {
    if (!isSnowplowScriptInPlace) return;

    try {
      initSnowplowTracker();

      setSnowplowInitialized(true);
      resolveRef.current?.();
    } catch (error) {
      logger.error(error, 'Failed to initialize Snowplow tracker');
      if (error instanceof Error) apmRum?.captureError(error);
      rejectRef.current?.();
    }
  }, [isSnowplowScriptInPlace]);

  // update consent context
  useEffect(() => {
    if (isSnowplowInitialized && consent) {
      addConsentContext(consent);
      setSnowplowConsentReady(true);
    }
  }, [consent, isSnowplowInitialized]);

  // set user id / clear user
  useEffect(() => {
    if (!isSnowplowInitialized) return;

    if (isAuthenticated && transientId) {
      setUser(transientId);
      if (age && isSubAccount) {
        addUserProfile(age);
      }
    } else if (isAuthenticated === false) {
      clearUser();
    }
    setSnowplowUserReady(true);
  }, [age, isAuthenticated, isSnowplowInitialized, isSubAccount, transientId]);

  return (
    <SnowplowContext.Provider value={contextValue}>
      <Script
        id="snowplow-load-script"
        onReady={() => setSnowplowScriptInPlace(true)}
      >
        {`;(function(p,l,o,w,i,n,g){if(!p[i]){p.GlobalSnowplowNamespace=p.GlobalSnowplowNamespace||[]; p.GlobalSnowplowNamespace.push(i);p[i]=function(){(p[i].q=p[i].q||[]).push(arguments) };p[i].q=p[i].q||[];n=l.createElement(o);g=l.getElementsByTagName(o)[0];n.async=1;n.crossOrigin=""; n.src=w;g.parentNode.insertBefore(n,g)}}(window,document,"script",${JSON.stringify(snowplowAnalyticsProperties.trackerUrl)},"yleSnowplow"));`}
      </Script>
      {children}
    </SnowplowContext.Provider>
  );
};

export function useSnowplow(): SnowplowContextType {
  return useContext(SnowplowContext);
}
